import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import ServiceLayout from '../../../components/layout/ServiceLayout';
import ServicePageSection from '../../../components/layout/ServicePageSection';

// UI components
import CompactFeatureCard from '../../../components/ui/CompactFeatureCard';
import ProcessStep from '../../../components/ui/ProcessStep';
import Card from '../../../components/ui/Card';
import FAQ from '../../../components/ui/FAQ';

// Section components
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * AI System Optimization Service Page - Clean and Professional Layout
 */
const AIOptimizationPage: React.FC = () => {
  // Filter relevant case studies
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.solution === 'AI System Optimization'
  );

  // Filter relevant FAQs
  const relevantFaqs = faqs.filter((_, index) => [2, 3, 4].includes(index));

  const keyBenefits = [
    {
      title: "Enhanced Performance",
      description: "Significantly improve the speed, accuracy, and reliability of your existing AI systems through expert fine-tuning and optimization techniques.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: "Increased ROI",
      description: "Extract maximum value from your AI investments by optimizing systems to deliver better results with fewer resources and lower operational costs.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: "Future-Proofed Systems",
      description: "Ensure your AI implementations remain effective as your business evolves, with scalable architecture and adaptable models that grow with your needs.",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  const optimizationProcess = [
    {
      step: 1,
      title: "Comprehensive System Audit",
      description: "We conduct a thorough analysis of your existing AI systems, identifying performance bottlenecks, accuracy issues, and optimization opportunities."
    },
    {
      step: 2,
      title: "Data Quality Assessment",
      description: "Our data scientists evaluate your training and production data, improving data quality, identifying biases, and enhancing feature engineering."
    },
    {
      step: 3,
      title: "Model Fine-Tuning",
      description: "We optimize your AI models through hyperparameter tuning, architecture improvements, and advanced training techniques to boost performance."
    },
    {
      step: 4,
      title: "Infrastructure Optimization",
      description: "Our engineers optimize your AI infrastructure for cost-efficiency, speed, and scalability, whether cloud-based, on-premises, or hybrid."
    },
    {
      step: 5,
      title: "Implementation & Testing",
      description: "We implement optimizations with minimal disruption and rigorously test to validate improvements in performance, accuracy, and reliability."
    },
    {
      step: 6,
      title: "Monitoring & Continuous Improvement",
      description: "We establish robust monitoring systems and provide recommendations for ongoing optimization to ensure sustained performance gains."
    }
  ];

  const optimizationAreas = [
    {
      title: "Model Performance Optimization",
      description: "Enhance AI model accuracy, prediction quality, and response times through advanced techniques like model distillation, quantization, and specialized algorithms."
    },
    {
      title: "Data Pipeline Efficiency",
      description: "Streamline data collection, preprocessing, and feature engineering workflows to improve data quality and reduce processing time and resource consumption."
    },
    {
      title: "Computational Resource Optimization",
      description: "Reduce cloud computing costs and hardware requirements while maintaining or improving performance through efficient resource allocation and utilization."
    },
    {
      title: "Integration Refinement",
      description: "Optimize how AI systems connect with other business systems, reducing latency, improving reliability, and enhancing two-way data flows."
    },
    {
      title: "User Experience Enhancement",
      description: "Improve how users interact with AI systems to increase adoption rates and satisfaction through interface refinements and workflow optimizations."
    },
    {
      title: "AI Governance & Compliance",
      description: "Optimize systems for transparency, explainability, and regulatory compliance while maintaining high performance and accuracy."
    }
  ];

  const optimizationOutcomes = [
    { value: "40%+", label: "Average improvement in model accuracy" },
    { value: "60%+", label: "Typical reduction in processing time" },
    { value: "35%+", label: "Average decrease in operational costs" }
  ];

  return (
    <ServiceLayout>
      <Hero
        title="AI System Optimization"
        subtitle="Fine-tune your existing AI implementations to maximize performance, accuracy, and ROI."
        ctaText="Optimize Your AI Systems"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Benefits Section */}
      <ServicePageSection 
        title="Maximize Your AI Investment" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {keyBenefits.map((benefit, index) => (
            <CompactFeatureCard
              key={benefit.title}
              title={benefit.title}
              description={benefit.description}
              icon={benefit.icon}
              delay={index * 0.1}
            />
          ))}
        </div>
      </ServicePageSection>

      {/* Our Optimization Process Section */}
      <ServicePageSection 
        title="Our AI Optimization Process" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {optimizationProcess.map((process, index) => (
            <motion.div
              key={process.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <ProcessStep 
                number={process.step} 
                title={process.title}
                description={process.description}
              />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Optimization Areas */}
      <ServicePageSection 
        title="Key Optimization Areas" 
        bgColor="gray"
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {optimizationAreas.map((area, index) => (
            <motion.div
              key={area.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="h-full p-6 hover:shadow-lg transition-shadow duration-200">
                <h3 className="text-lg font-semibold mb-3">{area.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{area.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Optimization Results */}
      <ServicePageSection 
        title="Typical Optimization Outcomes" 
        padding="md"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {optimizationOutcomes.map((outcome, index) => (
            <motion.div
              key={outcome.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="p-8">
                <div className="text-4xl font-bold text-primary mb-2">{outcome.value}</div>
                <p className="text-neutral-600">{outcome.label}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <ServicePageSection title="Optimization Success Stories" bgColor="gray" padding="md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {relevantCaseStudies.slice(0, 2).map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={study.url} className="block h-full">
                  <Card hover className="h-full p-6">
                    <div className="mb-3">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {study.solution}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold mb-2">{study.title}</h3>
                    <p className="text-neutral-600 text-sm mb-4">{study.excerpt}</p>
                    
                    <div className="space-y-2">
                      {study.results.slice(0, 2).map((result, idx) => (
                        <div key={idx} className="flex items-start text-sm">
                          <svg className="w-4 h-4 text-primary mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-neutral-600">{result}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <span className="inline-flex items-center text-primary font-medium text-sm">
                        Read full case study
                        <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <Link 
              to="/case-studies" 
              className="inline-flex items-center text-primary font-medium hover:text-primary-700 transition-colors"
            >
              View all case studies
              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </ServicePageSection>
      )}

      {/* FAQ Section */}
      <ServicePageSection 
        title="Frequently Asked Questions" 
        padding="md"
      >
        <div className="max-w-3xl mx-auto space-y-6">
          {relevantFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <FAQ items={[{ question: faq.question, answer: faq.answer }]} />
            </motion.div>
          ))}
        </div>
      </ServicePageSection>

      {/* Call to Action */}
      <CallToAction
        title="Ready to optimize your AI systems?"
        description="Contact us today to discuss how our AI optimization services can improve performance, reduce costs, and maximize your return on AI investments."
        ctaText="Get Started with Optimization"
        ctaLink="/contact"
      />
    </ServiceLayout>
  );
};

export default AIOptimizationPage;